import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import Map from 'ol/Map';
import View from 'ol/View';
import { fromLonLat } from 'ol/proj';
import BaseLayer from 'ol/layer/Base';
import TileLayer from 'ol/layer/Tile';
import type Feature from 'ol/Feature';
import type Geometry from 'ol/geom/Geometry';
import type VectorSource from 'ol/source/Vector';
import { makeBasemapLayer, makeRasterLayer, makeVectorTileLayer } from './layers';
import { makePhotoLayer } from './photoLayer';
import { probeFeatureSource, type LayerHealth } from './layerHealth';
import { fetchLayers, type ApiLayer } from '../api/layers';
import {
  createFolder as apiCreateFolder,
  deleteFolder as apiDeleteFolder,
  fetchFolders,
  renameFolder as apiRenameFolder,
  type ApiFolder,
} from '../api/workspaceFolders';
import { useNavigation } from '../navigation/NavigationContext';
import {
  fetchMeasurements,
  createMeasurement,
  patchMeasurement,
  deleteMeasurement as apiDeleteMeasurement,
  type ApiMeasurement,
} from '../api/measurements';
import {
  cloneMeasurementFeature,
  createMeasurementEntity,
  featureFromGeoJson,
  makeMeasurementsLayer,
  measurementGeometryToGeoJson,
  type MeasurementEntity,
  type MeasurementType,
} from './measurement';
import { FARM_LON, FARM_LAT } from '../config/env';

export type { MeasurementEntity, MeasurementType } from './measurement';
export type { LayerHealth } from './layerHealth';

export interface LayerEntry {
  id: string;
  title: string;
  layer: BaseLayer;
  kind: 'basemap' | 'vector' | 'raster' | 'photo';
  date?: string;
  /** Synthetic/test data (platform_db datasets.is_test). */
  isTest?: boolean;
  /** Rasters only: the user's own eye-toggle choice. `undefined` = follow the timeline bar (the
   * default); `true`/`false` = force shown/hidden regardless of the active date. */
  userVisible?: boolean;
  /** GeoServer layer name (no workspace prefix) for WMS-backed entries. */
  sourceName?: string;
  /** 'checking' only while GET /api/v1/layers or the Field Photos probe is still in flight —
   * everything else comes straight from the server (app/geoserver.py, v0.6.3), not a client-side
   * per-layer check anymore. A 'missing' or 'unreachable' entry is listed in the Scene Manager
   * with an inline marker and never requested, so it can't trigger the tile-error banner. */
  health: LayerHealth | 'checking';
}

/** Only layers that actually exist on the server can be shown/hidden. */
export function isLayerAvailable(entry: LayerEntry): boolean {
  return entry.health === 'available';
}

// Placeholder "current user" for created-by lines, same convention already
// established in the top bar / Field Photos panel (design/hub/hub.html's
// own avatar chip uses the same initials for the same real account).
export const CURRENT_USER = 'HA';

interface MapContextValue {
  map: Map;
  layers: LayerEntry[];
  /** Eye toggle. For rasters this is an override of the timeline: `visible` forces shown/hidden.
   * With `followTimeline` (group eyes), showing a raster instead clears the override so the
   * timeline decides again. */
  setLayerVisible: (id: string, visible: boolean, opts?: { followTimeline?: boolean }) => void;
  /** True once GET /api/v1/layers itself could not be reached (as opposed to individual layers
   * being unpublished, or Field Photos specifically being unreachable). */
  geoserverUnreachable: boolean;
  /** Re-run GET /api/v1/layers and the Field Photos probe (Scene Manager's Refresh). */
  recheckLayerHealth: () => void;
  /** Bumped whenever the timeline/compare effect changes raster visibility
   * on the OL layers directly. Consumers that read `layer.getVisible()`
   * during render (the Scene Manager rows) re-render off this — otherwise
   * they'd show the visibility from BEFORE the effect ran. */
  visibilityTick: number;
  activeDate: string;
  setActiveDate: (date: string) => void;
  compareMode: boolean;
  setCompareMode: (v: boolean) => void;
  compareDates: [string, string];
  setCompareDates: (dates: [string, string]) => void;
  swipePosition: number; // 0..1
  setSwipePosition: (v: number) => void;
  dates: string[];
  /** The date the map opens on: the latest REAL (non-test) imagery date, else the latest of any. */
  defaultDate: string;
  // Measurement tools + persisted measurement entities.
  activeMeasureTool: MeasurementType | null;
  setActiveMeasureTool: (tool: MeasurementType | null) => void;
  measurementsSource: VectorSource<Feature<Geometry>>;
  measurements: MeasurementEntity[];
  addMeasurement: (feature: Feature<Geometry>, type: MeasurementType) => MeasurementEntity;
  removeMeasurement: (id: string) => void;
  clearMeasurements: () => void;
  setMeasurementVisible: (id: string, visible: boolean) => void;
  updateMeasurementDescription: (id: string, description: string) => void;
  renameMeasurement: (id: string, name: string) => void;
  duplicateMeasurement: (id: string) => void;
  setMeasurementColor: (id: string, color: string) => void;
  selectedMeasurementId: string | null;
  selectMeasurement: (id: string | null) => void;
  /** The workspace the map is currently showing (null on the home page). Layers, measurements
   * and folders are all per-workspace. */
  workspaceSlug: string | null;
  /** This workspace's user-made folders (persisted server-side), in display order. */
  folders: ApiFolder[];
  /** Resolves once saved; rejects with FolderApiError (e.g. 409 = that name is taken). */
  addFolder: (name: string) => Promise<void>;
  renameFolder: (folderId: string, name: string) => Promise<void>;
  /** Deleting a folder never deletes its measurements -- they move out to "no folder". */
  removeFolder: (folderId: string) => Promise<void>;
  moveMeasurementToFolder: (measurementId: string, folderId: string | null) => void;
}

const MapContext = createContext<MapContextValue | null>(null);

function apiLayerEntry(l: ApiLayer): LayerEntry {
  const layer =
    l.kind === 'raster'
      ? makeRasterLayer({ name: l.source_name ?? '', title: l.title, date: l.date ?? '', product: 'orthomosaic' }, { visible: false })
      : makeVectorTileLayer({ name: l.source_name ?? '', title: l.title }, { visible: l.health === 'available' });
  return {
    id: `${l.kind}:${l.id}`,
    title: l.kind === 'raster' && l.date ? `${l.title} — ${l.date.slice(0, 10)}` : l.title,
    layer,
    kind: l.kind,
    date: l.date ?? undefined,
    isTest: l.is_test,
    sourceName: l.source_name ?? undefined,
    health: l.health,
  };
}

/** Rebuilds a MeasurementEntity + its OL feature from one GET /api/v1/measurements row
 * (v0.6.5) — the inverse of what addMeasurement()/the PATCH helpers below send. */
function apiMeasurementToEntity(m: ApiMeasurement): MeasurementEntity {
  const feature = featureFromGeoJson(m.geometry);
  const entity = createMeasurementEntity(feature, m.measurement_type, m.owner_username, m.measurement_id);
  entity.name = m.name;
  entity.description = m.description ?? '';
  entity.visible = m.visible;
  entity.color = m.color ?? undefined;
  entity.createdAt = m.created_at;
  entity.folderId = m.folder_id;
  feature.set('measurementName', m.name);
  feature.set('color', m.color ?? undefined);
  feature.set('hidden', !m.visible);
  return entity;
}

export function MapProvider({ children }: { children: ReactNode }) {
  const mapRef = useRef<Map | null>(null);
  const [layers, setLayers] = useState<LayerEntry[]>([]);
  // Timeline dates come from the rasters that are NOT known to be missing
  // from GeoServer — so the active date is always one whose imagery
  // exists. While the check is still running (or if GeoServer is down)
  // every configured date stays listed; only a confirmed 'missing' drops
  // out. Re-derived automatically, so publishing new surveys later needs
  // no frontend code change (ROADMAP.md v0.6.3's own required validation).
  const dates = useMemo(
    () =>
      Array.from(
        new Set(layers.filter((l) => l.kind === 'raster' && l.health !== 'missing' && l.date).map((l) => l.date!)),
      ).sort(),
    [layers],
  );
  // Open on the latest real image, not the latest test raster (test data is newer than the first
  // real satellite capture, so "latest overall" would land on a synthetic ortho).
  const defaultDate = useMemo(() => {
    const real = Array.from(
      new Set(layers.filter((l) => l.kind === 'raster' && !l.isTest && l.health !== 'missing' && l.date).map((l) => l.date!)),
    ).sort();
    return real[real.length - 1] ?? dates[dates.length - 1] ?? '';
  }, [layers, dates]);
  const [activeDate, setActiveDate] = useState<string>(defaultDate);
  const [compareMode, setCompareMode] = useState(false);
  const [compareDates, setCompareDates] = useState<[string, string]>([
    dates[0] ?? '',
    dates[dates.length - 1] ?? '',
  ]);
  const [swipePosition, setSwipePosition] = useState(0.5);
  const [activeMeasureTool, setActiveMeasureTool] = useState<MeasurementType | null>(null);
  const [measurements, setMeasurements] = useState<MeasurementEntity[]>([]);
  const [selectedMeasurementId, setSelectedMeasurementId] = useState<string | null>(null);
  // Everything workspace-scoped (layers, measurements, folders) follows the route's workspace.
  const { focusGroupId: workspaceSlug } = useNavigation();
  const [folders, setFolders] = useState<ApiFolder[]>([]);
  const measurementsSourceRef = useRef<VectorSource<Feature<Geometry>> | null>(null);
  // True once GET /api/v1/layers itself failed (not just an individual layer being missing).
  const [layersUnreachable, setLayersUnreachable] = useState(false);

  if (!mapRef.current) {
    const basemap = makeBasemapLayer();
    const photoLayer = makePhotoLayer();
    photoLayer.setVisible(false);
    // The measurements layer renders every persisted user-drawn shape, but
    // deliberately does NOT get its own LayerEntry/sceneTree row — each
    // measurement is its own row instead (buildSceneTree's "Workspace
    // content" group, driven by `measurements` below), same distinction as
    // Field Photos being one layer/many pins, just one level deeper.
    const { source: measurementsSource, layer: measurementsLayer } = makeMeasurementsLayer();
    measurementsSourceRef.current = measurementsSource;
    // Raster/vector layers are added once GET /api/v1/layers resolves (below) — the map itself
    // doesn't wait on that fetch to exist, so panning/zooming the basemap works immediately.
    const allLayers: BaseLayer[] = [basemap, photoLayer, measurementsLayer];
    mapRef.current = new Map({
      layers: allLayers,
      view: new View({
        center: fromLonLat([FARM_LON, FARM_LAT]),
        zoom: 15,
      }),
      controls: [],
    });
    setLayers([
      { id: 'basemap:osm', title: 'Basemap', layer: basemap, kind: 'basemap', health: 'available' },
      { id: 'photo:field-photos', title: 'Field Photos', layer: photoLayer, kind: 'photo', health: 'checking' },
    ]);
  }

  // ---- GET /api/v1/layers (v0.6.3) + the Field Photos probe (still client-side — a different
  // data source, Ente-fed WFS/GeoJSON, not part of the datasets/workspace_layers registry) ----
  // The raster/vector OL layers the last GET /api/v1/layers added to the map, so a refresh can
  // remove exactly those.
  const apiLayersOnMapRef = useRef<BaseLayer[]>([]);
  const [healthRun, setHealthRun] = useState(0);
  const layersWorkspaceRef = useRef<string | null>(null);
  useEffect(() => {
    if (!workspaceSlug) return; // on the home page: no map, nothing to load
    let cancelled = false;

    // Entering a different workspace: drop the previous workspace's raster/vector layers right
    // away, so they never linger on the map (or in the tree) while the new set is loading. A plain
    // refresh of the SAME workspace keeps them until the fresh response replaces them.
    if (layersWorkspaceRef.current !== workspaceSlug) {
      layersWorkspaceRef.current = workspaceSlug;
      const map = mapRef.current!;
      for (const layer of apiLayersOnMapRef.current) map.removeLayer(layer);
      apiLayersOnMapRef.current = [];
      setLayers((prev) => prev.filter((e) => e.kind === 'basemap' || e.kind === 'photo'));
      setLayersUnreachable(false);
    }

    fetchLayers(workspaceSlug)
      .then((apiLayers) => {
        if (cancelled) return;
        setLayersUnreachable(false);
        const newEntries = apiLayers.map(apiLayerEntry);
        // Mutate the OL map HERE, not inside a setLayers updater: React may invoke an updater
        // twice (StrictMode in dev), and a second addLayer of the same layer throws "Duplicate
        // item added to a unique collection". Remove whatever the last load added (a recheck can
        // legitimately change the set — a dataset can be added or removed server-side), then add
        // the fresh ones. Basemap/photo/measurements are never touched here.
        const map = mapRef.current!;
        for (const layer of apiLayersOnMapRef.current) map.removeLayer(layer);
        for (const entry of newEntries) map.addLayer(entry.layer);
        apiLayersOnMapRef.current = newEntries.map((e) => e.layer);
        setLayers((prev) => {
          // A refresh keeps the user's own raster eye choices.
          const chosen: Record<string, boolean | undefined> = {};
          for (const e of prev) chosen[e.id] = e.userVisible;
          return [
            ...prev.filter((e) => e.kind === 'basemap' || e.kind === 'photo'),
            ...newEntries.map((e) => ({ ...e, userVisible: chosen[e.id] })),
          ];
        });
      })
      .catch(() => {
        if (cancelled) return;
        setLayersUnreachable(true);
      });

    probeFeatureSource().then((health) => {
      if (cancelled) return;
      setLayers((prev) =>
        prev.map((entry) => {
          if (entry.kind !== 'photo' || entry.health === health) return entry;
          // Only reached on a health TRANSITION, so this never overrides a user's own toggle of
          // a layer that was and still is available.
          // Field Photos pins belong to the Field Photos workspace only.
          entry.layer.setVisible(health === 'available' && workspaceSlug === 'field-photos');
          return { ...entry, health };
        }),
      );
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [healthRun, workspaceSlug]);

  // The Field Photos layer exists once for the whole map; it only SHOWS in its own workspace.
  useEffect(() => {
    setLayers((prev) => {
      for (const e of prev) {
        if (e.kind === 'photo' && e.health === 'available') e.layer.setVisible(workspaceSlug === 'field-photos');
      }
      return prev;
    });
  }, [workspaceSlug]);

  // ---- GET /api/v1/measurements?workspace=… and the workspace's folders: every shape and folder
  // belongs to ONE workspace, so entering a workspace swaps both the React lists and the OL source
  // for that workspace's own persisted content (nothing leaks between workspaces). ----
  useEffect(() => {
    const source = measurementsSourceRef.current;
    if (!source) return;
    // Out with the previous workspace's shapes/folders immediately.
    source.clear();
    setMeasurements([]);
    setFolders([]);
    setSelectedMeasurementId(null);
    if (!workspaceSlug) return;

    let cancelled = false;
    fetchMeasurements(workspaceSlug)
      .then((apiMeasurements) => {
        if (cancelled) return; // the user already moved on to another workspace
        const hydrated = apiMeasurements.map((m) => apiMeasurementToEntity(m));
        for (const entity of hydrated) source.addFeature(entity.feature);
        setMeasurements(hydrated);
      })
      .catch((err: unknown) => {
        // Same "log, don't block the map" treatment as a Field Photos probe failure — measurements
        // are additive content on top of a map that otherwise works fine without them.
        console.error('Failed to load persisted measurements:', err);
      });
    fetchFolders(workspaceSlug)
      .then((apiFolders) => {
        if (!cancelled) setFolders(apiFolders);
      })
      .catch((err: unknown) => {
        console.error('Failed to load workspace folders:', err);
      });
    return () => {
      cancelled = true;
    };
  }, [workspaceSlug]);

  const geoserverUnreachable = layersUnreachable || layers.some((l) => l.kind === 'photo' && l.health === 'unreachable');

  // Keep the selected date(s) pointing at imagery that exists: if the
  // availability check drops the current active date (e.g. the latest
  // configured raster turns out to be unpublished), fall back to the latest
  // remaining date in the same month, else the latest overall.
  useEffect(() => {
    if (!dates.length) return;
    if (!dates.includes(activeDate)) {
      const sameMonth = dates.filter((d) => d.slice(0, 7) === activeDate.slice(0, 7));
      setActiveDate(sameMonth.length ? sameMonth[sameMonth.length - 1] : defaultDate);
    }
    if (!dates.includes(compareDates[0]) || !dates.includes(compareDates[1])) {
      setCompareDates([dates[0], dates[dates.length - 1]]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dates]);

  // Timeline / compare drive raster visibility centrally so the Scene
  // Manager's own per-layer toggles and the Timeline bar stay consistent
  // instead of fighting each other. Gated on health: a raster GeoServer
  // doesn't have (or can't serve) is never made visible, so no tiles are
  // ever requested for it.
  const [visibilityTick, setVisibilityTick] = useState(0);
  useEffect(() => {
    let changed = false;
    for (const entry of layers) {
      if (entry.kind !== 'raster') continue;
      const layer = entry.layer as TileLayer;
      const dateMatch = compareMode
        ? entry.date === compareDates[0] || entry.date === compareDates[1]
        : entry.date === activeDate;
      // The user's own eye choice beats the timeline; no choice -> the timeline decides.
      const next = isLayerAvailable(entry) && (entry.userVisible ?? dateMatch);
      if (layer.getVisible() !== next) {
        layer.setVisible(next);
        changed = true;
      }
    }
    if (changed) setVisibilityTick((t) => t + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDate, compareMode, compareDates, layers]);

  function setLayerVisible(id: string, visible: boolean, opts: { followTimeline?: boolean } = {}) {
    setLayers((prev) => {
      const entry = prev.find((l) => l.id === id);
      if (!entry || !isLayerAvailable(entry)) return prev;
      if (entry.kind === 'raster') {
        // Rasters: record the choice; the visibility effect above applies it (and keeps it when
        // the timeline moves). Pure update -- no OL mutation inside the updater.
        const userVisible = visible && opts.followTimeline ? undefined : visible;
        return prev.map((l) => (l.id === id ? { ...l, userVisible } : l));
      }
      entry.layer.setVisible(visible);
      return [...prev];
    });
  }

  // ---- measurements: persisted through the API (v0.6.5), not React-memory-only ----
  //
  // Create is optimistic (the feature is already on the map — the Draw interaction added it to
  // measurementsSource before addMeasurement() is even called, so there's no "not yet visible"
  // state to preserve): the entity appears immediately with a client-generated id, then
  // POST /api/v1/measurements runs in the background. On success the id is reconciled to the
  // server's real one; on failure the optimistic entity/feature is rolled back and the failure is
  // logged (same "log, don't block the map" treatment as a layer-health check failing). Attribute
  // edits (rename/recolour/visibility/description) update local state immediately too, but do NOT
  // roll back on a failed PATCH — these are low-stakes, and a full undo for every field edit would
  // add real complexity for a case (a 403 from losing ownership mid-session, or a dropped
  // connection) that's rare and self-correcting on the next page load. Delete waits for the API to
  // confirm first, since removing a feature from the map can't be un-shown as gracefully as an
  // optimistic add can be rolled back.

  function persistNewMeasurement(entity: MeasurementEntity) {
    createMeasurement({
      name: entity.name,
      description: entity.description || null,
      measurement_type: entity.type,
      color: entity.color ?? null,
      visible: entity.visible,
      geometry: measurementGeometryToGeoJson(entity.feature),
      workspace: workspaceSlug ?? 'general',
      folder_id: entity.folderId ?? null,
    })
      .then((saved) => {
        entity.feature.setId(saved.measurement_id);
        setMeasurements((prev) =>
          prev.map((m) =>
            m.id === entity.id
              ? { ...m, id: saved.measurement_id, createdBy: saved.owner_username, createdAt: saved.created_at }
              : m,
          ),
        );
        setSelectedMeasurementId((cur) => (cur === entity.id ? saved.measurement_id : cur));
      })
      .catch((err: unknown) => {
        console.error('Failed to save measurement — removing it from the map:', err);
        measurementsSourceRef.current?.removeFeature(entity.feature);
        setMeasurements((prev) => prev.filter((m) => m.id !== entity.id));
        setSelectedMeasurementId((cur) => (cur === entity.id ? null : cur));
      });
  }

  function addMeasurement(feature: Feature<Geometry>, type: MeasurementType): MeasurementEntity {
    const entity = createMeasurementEntity(feature, type, CURRENT_USER);
    setMeasurements((prev) => [...prev, entity]);
    persistNewMeasurement(entity);
    return entity;
  }

  function removeMeasurement(id: string) {
    apiDeleteMeasurement(id)
      .then(() => {
        setMeasurements((prev) => {
          const entry = prev.find((m) => m.id === id);
          if (entry) measurementsSourceRef.current?.removeFeature(entry.feature);
          return prev.filter((m) => m.id !== id);
        });
        setSelectedMeasurementId((cur) => (cur === id ? null : cur));
      })
      .catch((err: unknown) => {
        console.error('Failed to delete measurement (it has not been removed):', err);
      });
  }

  function clearMeasurements() {
    const toDelete = measurements; // event-handler closure, not a render read — safe to use directly
    Promise.allSettled(toDelete.map((m) => apiDeleteMeasurement(m.id))).then((results) => {
      const stillThere: MeasurementEntity[] = [];
      results.forEach((r, i) => {
        if (r.status === 'fulfilled') {
          measurementsSourceRef.current?.removeFeature(toDelete[i].feature);
        } else {
          console.error(`Failed to delete measurement ${toDelete[i].id} during clear-all:`, r.reason);
          stillThere.push(toDelete[i]);
        }
      });
      setMeasurements(stillThere);
    });
    setSelectedMeasurementId(null);
  }

  function setMeasurementVisible(id: string, visible: boolean) {
    setMeasurements((prev) =>
      prev.map((m) => {
        if (m.id !== id) return m;
        // The style function (measurement.ts) reads this property directly
        // off the OL feature — cheaper than round-tripping through React
        // state on every map render, and keeps OL's own paint loop
        // independent of React's.
        m.feature.set('hidden', !visible);
        m.feature.changed();
        return { ...m, visible };
      }),
    );
    patchMeasurement(id, { visible }).catch((err: unknown) => {
      console.error('Failed to save measurement visibility:', err);
    });
  }

  function updateMeasurementDescription(id: string, description: string) {
    setMeasurements((prev) => prev.map((m) => (m.id === id ? { ...m, description } : m)));
    patchMeasurement(id, { description }).catch((err: unknown) => {
      console.error('Failed to save measurement description:', err);
    });
  }

  function renameMeasurement(id: string, name: string) {
    const trimmed = name.trim();
    if (!trimmed) return;
    setMeasurements((prev) =>
      prev.map((m) => {
        if (m.id !== id) return m;
        m.feature.set('measurementName', trimmed);
        m.feature.changed();
        return { ...m, name: trimmed };
      }),
    );
    patchMeasurement(id, { name: trimmed }).catch((err: unknown) => {
      console.error('Failed to save measurement name:', err);
    });
  }

  function duplicateMeasurement(id: string) {
    setMeasurements((prev) => {
      const src = prev.find((m) => m.id === id);
      if (!src) return prev;
      const feature = cloneMeasurementFeature(src);
      if (!feature) return prev;
      measurementsSourceRef.current?.addFeature(feature);
      const copy = createMeasurementEntity(feature, src.type, CURRENT_USER);
      copy.name = `${src.name} copy`;
      copy.description = src.description;
      copy.color = src.color;
      copy.folderId = src.folderId; // a copy sits next to the original
      feature.set('measurementName', copy.name);
      if (src.color) feature.set('color', src.color);
      persistNewMeasurement(copy);
      return [...prev, copy];
    });
  }

  function setMeasurementColor(id: string, color: string) {
    setMeasurements((prev) =>
      prev.map((m) => {
        if (m.id !== id) return m;
        m.feature.set('color', color);
        m.feature.changed();
        return { ...m, color };
      }),
    );
    patchMeasurement(id, { color }).catch((err: unknown) => {
      console.error('Failed to save measurement colour:', err);
    });
  }

  function selectMeasurement(id: string | null) {
    setSelectedMeasurementId(id);
  }

  // ---- workspace folders (persisted: platform_db.workspace_folders) ----
  // Folder writes wait for the server (a 409 "name taken" must reach the UI), then update state.
  async function addFolder(name: string): Promise<void> {
    if (!workspaceSlug) return;
    const saved = await apiCreateFolder(workspaceSlug, name);
    setFolders((prev) => [...prev, saved]);
  }

  async function renameFolder(folderId: string, name: string): Promise<void> {
    if (!workspaceSlug) return;
    const saved = await apiRenameFolder(workspaceSlug, folderId, name);
    setFolders((prev) => prev.map((f) => (f.folder_id === folderId ? saved : f)));
  }

  async function removeFolder(folderId: string): Promise<void> {
    if (!workspaceSlug) return;
    await apiDeleteFolder(workspaceSlug, folderId);
    setFolders((prev) => prev.filter((f) => f.folder_id !== folderId));
    // The server moved its measurements out of the folder; mirror that here.
    setMeasurements((prev) => prev.map((m) => (m.folderId === folderId ? { ...m, folderId: null } : m)));
  }

  function moveMeasurementToFolder(measurementId: string, folderId: string | null) {
    setMeasurements((prev) => prev.map((m) => (m.id === measurementId ? { ...m, folderId } : m)));
    patchMeasurement(measurementId, { folder_id: folderId }).catch((err: unknown) => {
      console.error('Failed to save measurement folder:', err);
    });
  }

  const value: MapContextValue = {
    map: mapRef.current,
    layers,
    setLayerVisible,
    geoserverUnreachable,
    recheckLayerHealth: () => setHealthRun((n) => n + 1),
    visibilityTick,
    activeDate,
    setActiveDate,
    compareMode,
    setCompareMode,
    compareDates,
    setCompareDates,
    swipePosition,
    setSwipePosition,
    dates,
    defaultDate,
    activeMeasureTool,
    setActiveMeasureTool,
    // Non-null once mapRef.current exists (same lazy-init pattern as `map`
    // itself just above) — measurementsSourceRef is always set in the same
    // block that creates mapRef.current.
    measurementsSource: measurementsSourceRef.current!,
    measurements,
    addMeasurement,
    removeMeasurement,
    clearMeasurements,
    setMeasurementVisible,
    updateMeasurementDescription,
    renameMeasurement,
    duplicateMeasurement,
    setMeasurementColor,
    selectedMeasurementId,
    workspaceSlug,
    folders,
    addFolder,
    renameFolder,
    removeFolder,
    moveMeasurementToFolder,
    selectMeasurement,
  };

  return <MapContext.Provider value={value}>{children}</MapContext.Provider>;
}

export function useMapContext(): MapContextValue {
  const ctx = useContext(MapContext);
  if (!ctx) throw new Error('useMapContext must be used within MapProvider');
  return ctx;
}
